// Shared types for the FE async protocol.
// Contract: apps/fe/src/hooks/planning/async-protocol.md.
// Components never `await`. Every async-shaped hook returns one of:
//   - QueryResult<T>      (server reads)
//   - ActionResult<V, T>  (server writes + util side-effects, same shape)
//   - a sync snapshot     (subscriptions; not in this file)

export const INVOCATION_STATUS = {
  NotInvoked: 'NotInvoked',
  Loading: 'Loading',
  Errored: 'Errored',
  Success: 'Success',
} as const;
export type InvocationStatus = typeof INVOCATION_STATUS[keyof typeof INVOCATION_STATUS];

export const QUERY_STATUS = {
  Loading: 'Loading',
  Errored: 'Errored',
  Success: 'Success',
} as const;
export type QueryStatus = typeof QUERY_STATUS[keyof typeof QUERY_STATUS];

// Distinguishes read-shaped action hooks (e.g. useSignedUrlFetch) from write-shaped
// ones (mutations). Determines the default dedupe behavior — see DEDUPE_MODE below.
export const ACTION_KIND = {
  Mutation: 'Mutation',
  Query: 'Query',
} as const;
export type ActionKind = typeof ACTION_KIND[keyof typeof ACTION_KIND];

// Behavior when invoke() is called while status === Loading.
//   - NoOp: ignore the new invocation; the in-flight call settles normally.
//   - CancelAndRestart: abort the in-flight call (and its handlers), start fresh.
// Defaults: Mutation -> NoOp, Query -> CancelAndRestart. Override per-hook via the
// dedupeMode option on useActionHook's input.
export const DEDUPE_MODE = {
  NoOp: 'NoOp',
  CancelAndRestart: 'CancelAndRestart',
} as const;
export type DedupeMode = typeof DEDUPE_MODE[keyof typeof DEDUPE_MODE];

export interface InvocationHandlers<TData, TError = unknown> {
  onLoadingStart?: () => void;
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  // Fires after onSuccess / onError, regardless of branch. Use for cleanup that
  // runs in both cases (re-enable a button, close a modal). Name mirrors React
  // Query's `onSettled` for consistency with the underlying engine.
  onSettled?: () => void;
}

export interface QueryOptions {
  enabled?: boolean;
  refetchInterval?: number;
  refetchOnFocus?: boolean;
}

export interface QueryResult<TData, TError = unknown> {
  data: TData | null;
  status: QueryStatus;
  error: TError | null;
  refetch: () => void;
}

export interface ActionResult<TVariables, TData, TError = unknown> {
  invoke: (variables: TVariables, handlers?: InvocationHandlers<TData, TError>) => void;
  status: InvocationStatus;
  data: TData | null;
  error: TError | null;
  reset: () => void;
}

const NOOP_REFETCH = (): void => undefined;

// Narrowed QueryResult for hooks that are never loading/errored. Data is the
// non-nullable T (not T | null) so consumers don't have to handle null cases
// that can't actually occur. Still IS-A QueryResult — assignable to Cqueryview
// and any QueryResult<T> consumer.
export interface SyncQueryResult<TData> {
  data: TData;
  status: typeof QUERY_STATUS.Success;
  error: null;
  refetch: () => void;
}

// Wraps synchronous client state in the QueryResult shape so UI-state hooks
// (useNavigation, useChatState, etc.) expose the same envelope as server reads.
// Returns SyncQueryResult so consumers see data as non-nullable T.
export function syncResult<T>(data: T): SyncQueryResult<T> {
  return { data, status: QUERY_STATUS.Success, error: null, refetch: NOOP_REFETCH };
}

// For projection lookups that may return null (id not found in loaded map),
// returns a regular QueryResult<T> where data: T | null. Status is always
// Success — null signals not-found, which is distinct from Loading/Errored.
export function syncLookupResult<T>(data: T | null): QueryResult<T> {
  return { data, status: QUERY_STATUS.Success, error: null, refetch: NOOP_REFETCH };
}

// Projects a parent QueryResult through a synchronous selector. Loading/Errored
// status and error propagate from the parent; data is null whenever the parent's
// data is null OR the selector returns null (e.g. id not in the loaded map).
export function deriveResult<TParent, TChild, TError = unknown>(
  parent: QueryResult<TParent, TError>,
  project: (data: TParent) => TChild | null,
): QueryResult<TChild, TError> {
  return {
    data: parent.data === null ? null : project(parent.data),
    status: parent.status,
    error: parent.error,
    refetch: parent.refetch,
  };
}
